<template>
    <div class="relative">
        <div class="relative inline-block w-full group">
            <input
                v-model="search"
                placeholder="Search"
                class="peer search-input pl-7 pr-9 py-3 w-full border-solid border dark-theme:border-0 border-grey-muted focus:border-primary-accent hover:border-primary-accent focus:outline-none disabled:border-grey-pastel disabled:hover:border-grey-pastel disabled:bg-white focus:ring-0 text-primary-text text-brand-md placeholder:text-grey-pastel placeholder:font-normal rounded-3xl"
                @keyup="(inputChanged)"
                @blur="(resetData)"
            />
            <div :class="['a-loader absolute right-[63px] top-3', {'is-loading': isLoading}]" />
            <stockland-icon
                icon="search"
                scale="4"
                classes="fill-stockland-blue text-primary w-full"
                container-classes="absolute right-5 top-1/2 -mt-2"
            />
        </div>
        <ul
            v-show="isOpen"
            class="absolute -bottom-1 translate-y-full overflow w-full list-none margin-0 padding-0 border-1 border-grey-light shadow-lg z-menu-overlay"
        >
            <li
                v-for="(item, i) in decoratedData"
                :key="item.placeId"
                :class="[
                    i === arrowCounter ? 'bg-info-900' : 'bg-white',
                    'flex flex-col m-0 py-3 px-5 hover:bg-info-900 text-grey-dark border-b border-grey-light leading-tight cursor-pointer',
                ]"
                @mousedown="setResult(item)"
            >
                <span class="" v-html="item.htmlText" />
                <span class="text-brand-xs text-grey-muted">{{ item.textLocation.replace(", Australia", "") }}</span>
            </li>
            <li
                v-if="decoratedData.length === 0 && !loading"
                class="flex flex-col m-0 py-3 px-5 border-b border-grey-light bg-white leading-tight cursor-pointer"
            >
                <span class="">No search results</span>
                <span class="text-brand-xs text-grey-muted">Location not found within 500km</span>
            </li>
        </ul>
    </div>
</template>

<script lang="ts">
import { computed,defineComponent, PropType, reactive, SetupContext, toRefs, watch } from 'vue';

import search from '../../search/search';
import GmapsMapPlaces from '../gmaps-map/gmaps-map-places';
import { AutocompletePrediction,LatLng  } from '../gmaps-map/interfaces';

import { LocationMapSearchProperties,LocationMapSearchState } from './interfaces';

function useLocationMapSearch(props: LocationMapSearchProperties, context: SetupContext) {
    // const { data: placeData, getData: getPlaceData } = useApiGet<LocationMapPlaceSearchApi>();
	
    // Preload library
    GmapsMapPlaces.places();

    let debounce: ReturnType<typeof setTimeout> | undefined;

    const state: LocationMapSearchState = reactive({
        decoratedData: [],
        placeData: undefined,
        isLoading: false,
        loading: true,
        search: '',
        arrowCounter: -1,
        isOpen: computed(() => (state.search.length >= 3) && !state.loading),
    });
    watch(
        () => state.placeData,
        (value) => {
            context.emit('route-location', {
                id: value.place_id,
                name: value.name,
                location: {
                    lat: value.geometry.location.lat(),
                    lng: value.geometry.location.lng(),
                }
            });
        }
    )
    async function inputChanged(event: any) {
        if (state.search.length < 3) {
            resetData();
            return;
        }
        if (event.code == "ArrowUp") {
            if (state.decoratedData.length > 0 && state.arrowCounter >= 0) {
                state.arrowCounter = state.arrowCounter === 0 ? state.decoratedData.length - 1 : state.arrowCounter - 1;
            }
            return;
        } else if (event.code == "ArrowDown") {
            if (state.decoratedData.length > 0) {
                state.arrowCounter = state.arrowCounter === state.decoratedData.length - 1 ? 0 : state.arrowCounter + 1;
            }
            return;
        }
        if (event.code == "Enter") {
            // Set autocomplete
            if(state.arrowCounter !== -1) {
                setResult(state.decoratedData[state.arrowCounter]);
            } else if(state.arrowCounter === -1 && state.decoratedData.length > 0) {
                setResult(state.decoratedData[0]);
            } else {
                // clear result
                resetData();
                context.emit('route-location');
                state.search = "";
            }
            return
        }
        if (event.code == "Escape") {
            state.arrowCounter = -1;
            return
        }
        resetData();
        // Quick debounce (lodash not importing)
        debounce = setTimeout(async () => {
            clearTimeout(debounce);
            const places = await GmapsMapPlaces.places(props.map);
            state.decoratedData = await places.getPlacePredictions(state.search, props.origin);
            state.loading = false;
        }, 400)
    }

    function resetData() {
        state.loading = true;
        state.arrowCounter = -1;
        state.decoratedData = [];
    }

    async function setResult(item: AutocompletePrediction) {
        resetData();
        state.search = item.title;
        const places = await GmapsMapPlaces.places(props.map);
        state.placeData = await places.getPlaceLocation(item.placeId);
    }

    return {
        state,
        inputChanged,
        setResult,
        resetData,
    };
}

export default defineComponent({
    name: 'location-map-search',
    props: {
        map: {
            type: Object as PropType<google.maps.Map | undefined>, // as MapController
            default: undefined,
        },
        origin: {
            type: Object as PropType<LatLng | undefined>,
            default: () => { return { lat: -34.397, lng: 150.644 } }
        },
    },
    setup(props: LocationMapSearchProperties, context: SetupContext) {
        const { state, ...methods } = useLocationMapSearch(props, context);
        return {
            ...toRefs(state),
            ...methods
        };
    },
});
</script>
<style lang="postcss"></style>
